import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Package, FolderKanban, BookOpen, Archive } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { countAvailableInventory } from '@/lib/api/inventory';

interface Stats {
  projects_open: number;
  packages_draft: number;
  suppliers: number;
  inventory_items: number;
}

export function DashboardPage() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      try {
        const [projects, packages, suppliers, inventoryCount] = await Promise.all([
          supabase
            .from('projects')
            .select('id', { count: 'exact', head: true })
            .eq('type', 'package'),
          supabase
            .from('gift_packages')
            .select('id', { count: 'exact', head: true })
            .eq('status', 'draft')
            .not('project_id', 'is', null),
          supabase.from('gift_suppliers').select('id', { count: 'exact', head: true }),
          countAvailableInventory(),
        ]);
        setStats({
          projects_open: projects.count ?? 0,
          packages_draft: packages.count ?? 0,
          suppliers: suppliers.count ?? 0,
          inventory_items: inventoryCount,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Laden mislukt');
      }
    }
    void load();
  }, []);

  const cards = [
    {
      label: 'Projecten open',
      value: stats?.projects_open ?? '—',
      icon: FolderKanban,
      to: '/projecten',
      accent: 'text-accent-600 dark:text-accent-400',
    },
    {
      label: 'Pakketten concept',
      value: stats?.packages_draft ?? '—',
      icon: Package,
      to: '/projecten',
      accent: 'text-emerald-600 dark:text-emerald-400',
    },
    {
      label: 'Leveranciers',
      value: stats?.suppliers ?? '—',
      icon: BookOpen,
      to: '/catalogus',
      accent: 'text-sky-600 dark:text-sky-400',
    },
    {
      label: 'Restvoorraad items',
      value: stats?.inventory_items ?? '—',
      icon: Archive,
      to: '/voorraad',
      accent: 'text-amber-600 dark:text-amber-400',
    },
  ];

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-bmade-950 dark:text-white">Dashboard</h1>
        <p className="text-sm text-bmade-600 dark:text-bmade-400">
          Overzicht van actieve projecten, catalogus en restvoorraad.
        </p>
      </header>

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {cards.map(({ label, value, icon: Icon, to, accent }) => (
          <Link
            key={label}
            to={to}
            className="group rounded-xl border border-bmade-200 bg-white p-4 transition-colors hover:border-bmade-300 hover:shadow-sm dark:border-bmade-800 dark:bg-bmade-900/60 dark:hover:border-bmade-700 dark:hover:bg-bmade-900"
          >
            <div className={`mb-2 ${accent}`}>
              <Icon className="h-5 w-5" />
            </div>
            <div className="text-2xl font-semibold text-bmade-950 dark:text-white">{value}</div>
            <div className="text-xs uppercase tracking-wider text-bmade-500 dark:text-bmade-400">
              {label}
            </div>
          </Link>
        ))}
      </div>

      <section className="rounded-xl border border-bmade-200 bg-white p-6 dark:border-bmade-800 dark:bg-bmade-900/40">
        <h2 className="mb-2 text-lg font-semibold text-bmade-950 dark:text-white">Snelstart</h2>
        <p className="mb-4 text-sm text-bmade-600 dark:text-bmade-400">
          Begin met een nieuw project vanuit een inspiratiegesprek, of duik in de catalogus.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/projecten"
            className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-accent-600"
          >
            <FolderKanban className="h-4 w-4" />
            Naar projecten
          </Link>
          <Link
            to="/catalogus"
            className="inline-flex items-center gap-2 rounded-md border border-bmade-200 px-3 py-2 text-sm text-bmade-700 transition-colors hover:border-bmade-400 hover:bg-bmade-100 dark:border-bmade-700 dark:text-bmade-200 dark:hover:border-bmade-500 dark:hover:bg-bmade-800/50"
          >
            <BookOpen className="h-4 w-4" />
            Catalogus openen
          </Link>
        </div>
      </section>
    </div>
  );
}
